import { __decorate, __metadata } from 'tslib';
import { InjectorService } from '@tencent/ioc';
import { ProxyRegistry, nameOf, getInheritedClass } from '@tencent/workbench-core';
import { Service } from '../../decorators/class/service.js';
import { ControllerRegistry } from '../../registries/controller-registry.js';
import { ControllerBuilder } from '../../providers/middleware/controller-builder.js';
import { IControllerService } from './controller-service.interface.js';
import { ServiceCollection } from '../service-collection.js';
import { StateManager } from '../store/state-manager.js';
import { ReplaySubject, Subject } from 'rxjs';
import { WorkbenchMountSettings } from '../settings/auto-sync-settings.js';
import { bufferTime } from 'rxjs/operators';
import { updateUIStateWithServiceCollection } from '../update-ui-state-with-service-collection.js';
import { $logger } from '../../controllers/logger/logger.js';
import { FeatureFlagService } from '../feature/feature-flag-service.js';

/**
 * 用于管理 Controller 的服务
 *
 * @public
 */
let ControllerService = class ControllerService extends ProxyRegistry {
    constructor(serviceCollection, store, featureFlagService) {
        super(ControllerRegistry);
        this.serviceCollection = serviceCollection;
        this.store = store;
        this.featureFlagService = featureFlagService;
        this.controllerSettingChange = new ReplaySubject();
        this.postConstructorSubject = new Subject();
        this.affectedUIServiceMap = new WeakMap();
        this.affectedUIService = new Set();
        this.store.register(WorkbenchMountSettings.Controllers, []).subscribe(this.controllerSettingChange);
        this.postConstructorSubject
            .asObservable()
            .pipe(bufferTime(20))
            .subscribe((provideArr) => {
            const targetUIService = new Set(provideArr.flatMap(provide => { var _a; return Array.from((_a = this.affectedUIServiceMap.get(provide)) !== null && _a !== void 0 ? _a : []); }));
            updateUIStateWithServiceCollection(this.serviceCollection, targetUIService);
        });
        this.postDepsConstructorCallback = (provide) => {
            this.postConstructorSubject.next(provide);
        };
    }
    /**
     * 监听增量controller变化
     * @internal
     */
    subscribeToControllerChange() {
        if (this.controllerSettingChange.observers.length === 0) {
            this.controllerSettingChange.subscribe((controllers) => {
                this.affectedUIService.clear();
                this.buildControllers(controllers, true);
            });
        }
    }
    invoke(target, locals = new Map(), designParamTypes) {
        var _a;
        return InjectorService.invoke((_a = target.provide) !== null && _a !== void 0 ? _a : target, locals, designParamTypes);
    }
    /**
     * 构建 controllers
     * @param controllers - 需要增量构建的 controller, 不传会把已注册的controller全部 build一遍
     * @param updateImmediately - 是否立刻更新 ui
     * @public
     */
    buildControllers(controllers, updateImmediately = true) {
        if (!controllers) {
            ControllerRegistry.forEach((provider) => {
                this.buildControllerByProvider(provider, false);
            });
        }
        else {
            controllers.forEach((provider) => {
                this.buildController(provider, false);
            });
        }
        if (updateImmediately) {
            this.serviceCollection.getControllerRelatedUIServices().forEach((service, name) => {
                if (this.affectedUIService.has(name)) {
                    service.updateState();
                }
            });
        }
    }
    /**
     * 构建单个 controller
     * @param target - 要构建的controller
     * @param updateImmediately - 是否立刻更新ui
     * @public
     */
    buildController(target, updateImmediately = true) {
        var _a;
        const provider = ControllerRegistry.get(target);
        if (!provider) {
            throw new ReferenceError(`Controller ${nameOf(target)} is not loaded, make sure target is decorated as Controller`);
        }
        if (provider.type !== 'controller') {
            throw new TypeError(`Provider ${nameOf(target)} is not a controller.`);
        }
        if (!this.featureFlagService.getFlag(`controller.${(_a = provider.name) !== null && _a !== void 0 ? _a : ''}`)) {
            return;
        }
        this.buildControllerByProvider(provider, updateImmediately);
    }
    buildControllerByProvider(provider, updateImmediately) {
        let parent = getInheritedClass(provider.provide);
        while (parent && ControllerRegistry.has(parent)) {
            const parentProvider = ControllerRegistry.get(parent);
            new ControllerBuilder(parentProvider, this.serviceCollection, this.postDepsConstructorCallback).build();
            parent = getInheritedClass(parent);
        }
        const builder = new ControllerBuilder(provider, this.serviceCollection, this.postDepsConstructorCallback).build(updateImmediately);
        if (provider.instance === undefined) {
            try {
                provider.instance = InjectorService.get(provider.useClass);
            }
            catch (e) {
                $logger.error(`Failed to construct controller ${provider.className}, ${e}`);
                return;
            }
        }
        // 返回受到影响的ui service name
        const curAffectedUIServices = builder.getAffectedUIServices();
        this.affectedUIServiceMap.set(provider.provide, curAffectedUIServices);
        for (const affectedItem of curAffectedUIServices) {
            this.affectedUIService.add(affectedItem);
        }
    }
};
ControllerService = __decorate([
    Service({ overridable: true, provide: IControllerService }),
    __metadata("design:paramtypes", [ServiceCollection,
        StateManager,
        FeatureFlagService])
], ControllerService);

export { ControllerService };
//# sourceMappingURL=controller-service.js.map
